import { useEffect, useState } from 'react';
import { useRoomStore } from '@features/room';
import { useModerationStore } from '@features/moderation/moderationStore';
import {
  Panel,
  ActionButton,
  ApproveIcon,
  DenyIcon,
  MicOffIcon,
  MicIcon,
  PromoteIcon,
  DemoteIcon,
  RemoveIcon,
  StageIcon,
  RemoveStageIcon,
  LockIcon,
  UnlockIcon,
  ModeratorIcon,
} from '@shared/ui';
import type { BackendParticipant } from '@shared/types';

export function ParticipantsPanel() {
  const connectionState = useRoomStore((s) => s.connectionState);
  const meetingId = useRoomStore((s) => s.meetingId);
  const myParticipantId = useRoomStore((s) => s.participantId);

  const participants = useModerationStore((s) => s.participants);
  const myRole = useModerationStore((s) => s.myRole);
  const isRoomLocked = useModerationStore((s) => s.isRoomLocked);
  const isLoading = useModerationStore((s) => s.isLoading);
  const error = useModerationStore((s) => s.error);
  const init = useModerationStore((s) => s.init);
  const reset = useModerationStore((s) => s.reset);
  const approve = useModerationStore((s) => s.approve);
  const deny = useModerationStore((s) => s.deny);
  const mute = useModerationStore((s) => s.mute);
  const unmute = useModerationStore((s) => s.unmute);
  const promote = useModerationStore((s) => s.promote);
  const demote = useModerationStore((s) => s.demote);
  const remove = useModerationStore((s) => s.remove);
  const approveToStage = useModerationStore((s) => s.approveToStage);
  const removeFromStage = useModerationStore((s) => s.removeFromStage);
  const lockRoom = useModerationStore((s) => s.lockRoom);
  const unlockRoom = useModerationStore((s) => s.unlockRoom);

  const isConnected = connectionState === 'connected';
  const isHost = myRole === 'Host';
  const isModerator = myRole === 'Host' || myRole === 'Moderator';

  const [pendingParticipantIds, setPendingParticipantIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (isConnected && meetingId && myParticipantId) {
      void init(meetingId, myParticipantId);
    }
    if (!isConnected) {
      reset();
    }
  }, [isConnected, meetingId, myParticipantId, init, reset]);

  if (!isConnected || !isModerator) {
    return null;
  }

  const pending = participants.filter((p) => p.status === 'PendingApproval');
  const active = participants.filter((p) => p.status === 'Approved');

  const handleApprove = async (id: string) => {
    if (pendingParticipantIds.has(id)) return;
    setPendingParticipantIds((prev) => new Set(prev).add(id));
    try {
      await approve(id);
    } finally {
      setPendingParticipantIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const handleDeny = async (id: string, name: string) => {
    if (pendingParticipantIds.has(id)) return;
    if (!window.confirm(`Deny ${name}'s request to join?`)) return;
    setPendingParticipantIds((prev) => new Set(prev).add(id));
    try {
      await deny(id);
    } finally {
      setPendingParticipantIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <Panel title="PARTICIPANTS">
      {isHost && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-surface-border bg-surface-2 px-3 py-2">
          <span className="text-sm text-text-secondary">
            {isRoomLocked ? 'Room is locked' : 'Room is open'}
          </span>
          <ActionButton
            variant={isRoomLocked ? 'neutral' : 'danger'}
            icon={isRoomLocked ? <UnlockIcon className="h-3.5 w-3.5" /> : <LockIcon className="h-3.5 w-3.5" />}
            onClick={() => void (isRoomLocked ? unlockRoom() : lockRoom())}
            className="flex-none w-auto min-h-0 px-3 py-1.5 text-xs"
          >
            {isRoomLocked ? 'Unlock' : 'Lock'}
          </ActionButton>
        </div>
      )}

      {error && (
        <p className="mb-3 rounded-lg border border-status-live/30 bg-status-live/10 px-3 py-2 text-xs text-status-live">
          {error}
        </p>
      )}

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      {!isLoading && pending.length === 0 && active.length === 0 && (
        <p className="text-sm text-text-muted">No participants yet</p>
      )}

      {pending.length > 0 && (
        <div className="mb-4">
          <div className="mb-2 text-xs font-medium text-text-secondary">Waiting for approval</div>
          <div className="space-y-2">
            {pending.map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between rounded-lg border border-surface-border bg-surface-2 px-3 py-2"
              >
                <span className="truncate text-sm text-text-primary">{p.displayName}</span>
                <div className="flex shrink-0 gap-1.5">
                  <ActionButton
                    variant="success"
                    icon={<ApproveIcon className="h-3.5 w-3.5" />}
                    onClick={() => void handleApprove(p.id)}
                    disabled={pendingParticipantIds.has(p.id)}
                    className="flex-none w-auto min-h-0 px-2.5 py-1.5 text-xs"
                  >
                    Approve
                  </ActionButton>
                  <ActionButton
                    variant="danger"
                    icon={<DenyIcon className="h-3.5 w-3.5" />}
                    onClick={() => void handleDeny(p.id, p.displayName)}
                    disabled={pendingParticipantIds.has(p.id)}
                    className="flex-none w-auto min-h-0 px-2.5 py-1.5 text-xs"
                  >
                    Deny
                  </ActionButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-medium text-text-secondary">In the room</div>
          <div className="space-y-2">
            {active.map((p) => (
              <ParticipantRow
                key={p.id}
                participant={p}
                isSelf={p.id === myParticipantId}
                isHost={isHost}
                isModerator={isModerator}
                onMute={() => mute(p.id)}
                onUnmute={() => unmute(p.id)}
                onPromote={() => promote(p.id)}
                onDemote={() => demote(p.id)}
                onRemove={() => remove(p.id)}
                onApproveToStage={() => approveToStage(p.id)}
                onRemoveFromStage={() => removeFromStage(p.id)}
              />
            ))}
          </div>
        </div>
      )}
    </Panel>
  );
}

function ParticipantRow({
  participant,
  isSelf,
  isHost,
  isModerator,
  onMute,
  onUnmute,
  onPromote,
  onDemote,
  onRemove,
  onApproveToStage,
  onRemoveFromStage,
}: {
  participant: BackendParticipant;
  isSelf: boolean;
  isHost: boolean;
  isModerator: boolean;
  onMute: () => Promise<void>;
  onUnmute: () => Promise<void>;
  onPromote: () => Promise<void>;
  onDemote: () => Promise<void>;
  onRemove: () => Promise<void>;
  onApproveToStage: () => Promise<void>;
  onRemoveFromStage: () => Promise<void>;
}) {
  const isParticipantModerator = participant.role === 'Moderator';
  const isOnStage = participant.role === 'OnCameraGuest' || participant.role === 'Host';
  const liveKitParticipant = useRoomStore((s) =>
    s.participants.find(
      (p) => p.backendId === participant.id || p.name === participant.displayName
    )
  );
  const isMicOn = liveKitParticipant?.isMicEnabled ?? true;

  const [pendingAction, setPendingAction] = useState<string | null>(null);

  const runAction = async (key: string, action: () => Promise<void>) => {
    if (pendingAction) return;
    setPendingAction(key);
    try {
      await action();
    } finally {
      setPendingAction(null);
    }
  };

  return (
    <div className="rounded-lg border border-surface-border bg-surface-2 px-3 py-2">
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className="truncate text-sm text-text-primary">{participant.displayName}</span>
        {isParticipantModerator && <ModeratorIcon className="h-3.5 w-3.5 shrink-0 text-accent" />}
        {isSelf && <span className="shrink-0 text-xs text-text-muted">(you)</span>}
      </div>

      {!isSelf && (
        <div className="flex flex-wrap gap-1.5">
          {isMicOn ? (
            <ActionButton
              variant="neutral"
              icon={<MicOffIcon className="h-3.5 w-3.5" />}
              onClick={() => void runAction('mute', onMute)}
              disabled={pendingAction === 'mute'}
              className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
            >
              Mute
            </ActionButton>
          ) : (
            <ActionButton
              variant="neutral"
              icon={<MicIcon className="h-3.5 w-3.5" />}
              onClick={() => void runAction('unmute', onUnmute)}
              disabled={pendingAction === 'unmute'}
              className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
            >
              Unmute
            </ActionButton>
          )}

          {isOnStage ? (
            <ActionButton
              variant="neutral"
              icon={<RemoveStageIcon className="h-3.5 w-3.5" />}
              onClick={() => void runAction('removeFromStage', onRemoveFromStage)}
              disabled={pendingAction === 'removeFromStage'}
              className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
            >
              Remove from stage
            </ActionButton>
          ) : (
            <ActionButton
              variant="neutral"
              icon={<StageIcon className="h-3.5 w-3.5" />}
              onClick={() => void runAction('approveToStage', onApproveToStage)}
              disabled={pendingAction === 'approveToStage'}
              className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
            >
              Add to stage
            </ActionButton>
          )}

          {isHost && (
            <>
              {isParticipantModerator ? (
                <ActionButton
                  variant="neutral"
                  icon={<DemoteIcon className="h-3.5 w-3.5" />}
                  onClick={() => void runAction('demote', onDemote)}
                  disabled={pendingAction === 'demote'}
                  className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
                >
                  Demote
                </ActionButton>
              ) : (
                <ActionButton
                  variant="neutral"
                  icon={<PromoteIcon className="h-3.5 w-3.5" />}
                  onClick={() => {
                    if (isOnStage) {
                      const confirmed = window.confirm(
                        `${participant.displayName} is currently on stage. Promoting to Moderator will remove them from the stage. Continue?`
                      );
                      if (!confirmed) return;
                    }
                    void runAction('promote', onPromote);
                  }}
                  disabled={pendingAction === 'promote'}
                  className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
                >
                  Promote
                </ActionButton>
              )}

              {isModerator && (
                <ActionButton
                  variant="danger"
                  icon={<RemoveIcon className="h-3.5 w-3.5" />}
                  onClick={() => {
                    if (window.confirm(`Remove ${participant.displayName} from the meeting?`)) {
                      void runAction('remove', onRemove);
                    }
                  }}
                  disabled={pendingAction === 'remove'}
                  className="flex-none w-auto min-h-0 px-2 py-1 text-xs"
                >
                  Remove
                </ActionButton>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}